/**
 * Format a Storyblok story date (stored UTC, e.g. "2025-06-01 10:00:00" or an
 * ISO string) as an ISO 8601 value carrying the Europe/Berlin offset, matching
 * the format Open Graph consumers expect for `article:published_time` and
 * `article:modified_time` (e.g. "2025-06-01T12:00:00+02:00"). The offset is
 * computed DST-aware via Intl.
 *
 * Returns `undefined` when the input is empty or unparseable so the caller can
 * omit the tag rather than emit an invalid/empty value.
 *
 * Note: the target timezone is an open question (see ECOM-2071 plan) — Europe/Berlin
 * is the editorial timezone for both brands today.
 */
export function formatOgDateTime(input?: string | null): string | undefined {
  if (!input) {
    return undefined
  }

  const normalized = input.includes('T') ? input : input.replace(' ', 'T')
  const hasZone = /([zZ])|([+-]\d{2}:?\d{2})$/.test(normalized)
  const date = new Date(hasZone ? normalized : `${normalized}Z`)
  if (Number.isNaN(date.getTime())) {
    return undefined
  }

  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone: 'Europe/Berlin',
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
      .formatToParts(date)
      .map((part) => [part.type, part.value]),
  ) as Record<string, string>

  // Some runtimes return '24' for midnight; normalize to '00'.
  const hour = parts.hour === '24' ? '00' : parts.hour

  const wallClockAsUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(hour),
    Number(parts.minute),
    Number(parts.second),
  )
  const offsetMinutes = Math.round((wallClockAsUtc - date.getTime()) / 60000)
  const sign = offsetMinutes >= 0 ? '+' : '-'
  const absOffset = Math.abs(offsetMinutes)
  const offsetHours = String(Math.floor(absOffset / 60)).padStart(2, '0')
  const offsetMins = String(absOffset % 60).padStart(2, '0')

  return `${parts.year}-${parts.month}-${parts.day}T${hour}:${parts.minute}:${parts.second}${sign}${offsetHours}:${offsetMins}`
}
